import { Component, Input, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { PosthogService } from 'src/app/analytics/services/posthog.service';
import { RecipeOptionModalService } from 'src/app/recipe-option-modal/recipe-option-modal.service';
import { ApiService } from 'src/app/service/api.service';
import { Recipe } from 'src/Model/Product-Interfaces';

@Component({
  selector: 'app-new-recipe-card',
  templateUrl: './new-recipe.component.html',
  styleUrls: ['./new-recipe.component.scss'],
})
export class NewRecipeComponent implements OnInit {
  @Input() addTocart: boolean = true;
  @Input() isPromotedItem: boolean = false;
  @Input() recipes: Partial<Recipe>[] = [];
  /** popular = image strip · main = one card, a row per dish · recommended =
      compact thumbnail rows · tile = photo grid (new-dine-in on desktop) */
  @Input() card: 'popular' | 'main' | 'recommended' | 'tile' = 'main';

  /**
   * Digital paper menu (new-dine-in): the whole card/row opens the options
   * modal in its read-only mode instead of an Add button, and nothing here
   * touches the cart. The modal reads the same flag as `isDineIn`.
   */
  @Input() readOnly: boolean = false;
  /** main card only: price and a round + side by side instead of the price
      over a full "Add" button — for dense lists (header search results) */
  @Input() compact: boolean = false;

  /** Main rows clamp long descriptions to three lines; these rows have been
      expanded with "Read more". Keyed by RecipeId so it survives re-renders. */
  private expandedDesc = new Set<number>();

  /** Roughly three lines at the row's width — under this the clamp never
      bites, so no toggle is offered. */
  readonly longDescription = 180;

  /** *ngFor identity: switching category or delivery type hands in a new
      array — rows of the same dish are updated in place, not rebuilt */
  trackRecipe(index: number, recipe: Partial<Recipe>) {
    return recipe?.RecipeId ?? index;
  }

  isLongDescription(recipe: Partial<Recipe>): boolean {
    return (recipe?.RecipeDescription?.length || 0) > this.longDescription;
  }

  isDescExpanded(recipe: Partial<Recipe>): boolean {
    return this.expandedDesc.has(recipe?.RecipeId as number);
  }

  toggleDesc(recipe: Partial<Recipe>, event: Event) {
    event.stopPropagation();
    const id = recipe?.RecipeId as number;
    if (this.expandedDesc.has(id)) this.expandedDesc.delete(id);
    else this.expandedDesc.add(id);
  }

  CurrencySymbol: string;
  SelectedButtonColor: string;
  SelectedButtonTextColor: string;

  RecipeNewPriceText: string = 'Now';
  RecipeOldPriceText: string = 'Before';

  ItemBackgroundColor;
  RecipeOldPriceTextColor: string = '#696868';
  RecipeDiscountLabelBGColor: string = '#198754';
  RecipeDiscountLabelTextColor: string = 'white';

  subs: Subscription[] = [];

  constructor(
    private apiService: ApiService,
    private optionModal: RecipeOptionModalService,
    private posthog: PosthogService
  ) {}

  ngOnInit(): void {
    this.getWebCustomer();
  }

  getWebCustomer() {
    const sub = this.apiService.getWebcustomer$.subscribe((res) => {
      if (!res) {
        return;
      }

      this.CurrencySymbol = res.TemplateData.CurrencySymbol || 'GBP';

      this.SelectedButtonColor =
        res.TemplateData.DynamicStyle.SecondaryColors.SelectedButtonColor;

      this.SelectedButtonTextColor =
        res.TemplateData.DynamicStyle.SecondaryColors.SelectedButtonTextColor;

      this.RecipeOldPriceText =
        res.TemplateData?.Menu?.DiscountedItemsSettings.Recipe.RecipeOldPriceText;

      this.RecipeNewPriceText =
        res.TemplateData?.Menu?.DiscountedItemsSettings.Recipe.RecipeNewPriceText;

      this.RecipeOldPriceTextColor =
        res.TemplateData?.Menu?.DiscountedItemsSettings.Recipe.RecipeOldPriceTextColor;

      this.RecipeDiscountLabelBGColor =
        res.TemplateData?.Menu?.DiscountedItemsSettings.Recipe.RecipeDiscountLabelBGColor;

      this.RecipeDiscountLabelTextColor =
        res.TemplateData?.Menu?.DiscountedItemsSettings.Recipe.RecipeDiscountLabelTextColor;

      this.ItemBackgroundColor =
        res.TemplateData.DynamicStyle.SecondaryColors?.ItemBackgroundColor;
    });

    this.subs.push(sub);
  }

  /**
   * A dish without a photo keeps a full image slot so titles and prices line
   * up across a row; the slot is filled with one of six soft tones picked
   * from the recipe id — stable across reloads, varied across a row — and
   * the dish's initial. `ui-swatch-<n>` is styled in the card stylesheet.
   */
  swatch(recipe: Partial<Recipe>): string {
    const seed = Number(recipe?.RecipeId) || (recipe?.RecipeTitle || '').length;
    return `ui-swatch-${seed % 6}`;
  }

  initial(recipe: Partial<Recipe>): string {
    return (recipe?.RecipeTitle || '').trim().charAt(0).toUpperCase();
  }

  /** Read-only rows hint at what the modal will show; extras may arrive as a
      JSON string, an object, or an empty string — only a non-empty one counts. */
  hasOptions(recipe: Partial<Recipe>): boolean {
    const extras: any = recipe?.RecipeExtraObject;
    if (!extras) return false;
    if (typeof extras === 'string') return extras.trim().length > 2;
    return Object.keys(extras).length > 0;
  }

  getRecipeModal(recipe: Partial<Recipe>) {
    this.posthog.captureEvent(
      this.readOnly ? 'DineIn_Item_Viewed' : 'Item_Add_to_Cart_Clicked',
      {
        RecipeId: recipe.RecipeId,
        RecipeTitle: recipe.RecipeTitle,
        Category: recipe.CategoryTitle,
        Unit_price: recipe.RecipePrice,
      }
    );

    // the extras flattening lives in the modal service so the promotion card
    // can open the same modal for a free dish
    this.optionModal.openForRecipe(recipe, {
      dineIn: this.readOnly,
      isPromotionItem: this.isPromotedItem,
      promotionItemId: recipe.PromotionItemId,
    });
  }
}
